<script setup lang="ts">
import { computed, ref, watch } from 'vue'

/**
 * A typed number with its unit and, when it is unusable, the reason. Placed in a
 * `FormRow`, which styles the input and the message.
 *
 * Emits NaN for a blank field rather than 0: an empty storage size is a
 * mistake to point out, not a free bucket.
 */
const props = withDefaults(
  defineProps<{
    id: string
    modelValue: number
    unit: string
    valid: (value: number) => boolean
    error: string
    step?: string
    /** Only when the row's visible label does not already name this field. */
    label?: string | undefined
  }>(),
  { step: '1', label: undefined },
)
const emit = defineEmits<{ 'update:modelValue': [number] }>()

// v-model on a number input may hand back a number or a string.
const parse = (value: string | number): number =>
  String(value).trim() === '' ? NaN : Number(value)

// A draft, so a cleared field stays cleared instead of snapping back.
const draft = ref<string | number>(String(props.modelValue))
watch(
  () => props.modelValue,
  (value) => {
    if (!Object.is(parse(draft.value), value)) draft.value = String(value)
  },
)
const invalid = computed(() => !props.valid(parse(draft.value)))
</script>

<template>
  <input
    :id="id"
    v-model="draft"
    class="number-field mono"
    type="number"
    min="0"
    :step="step"
    :inputmode="step === '1' ? 'numeric' : 'decimal'"
    autocomplete="off"
    :aria-label="label"
    :aria-invalid="invalid"
    :aria-describedby="invalid ? `${id}-error` : `${id}-unit`"
    @input="emit('update:modelValue', parse(draft))"
  />
  <span :id="`${id}-unit`" class="form-row__muted">{{ unit }}</span>
  <p v-if="invalid" :id="`${id}-error`" class="form-row__error number-field__error" role="alert">
    {{ error }}
  </p>
</template>

<style scoped>
.number-field {
  width: 8rem;
}

/* Inside the row's controls, which wrap: the message takes a line of its own. */
.number-field__error {
  flex-basis: 100%;
}
</style>
